import { useClosePopupService, useGetPopupService } from 'app/utils/services/homePageService';
import { startTransition, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

const useNotificationModal = () => {
  const { t } = useTranslation();

  const { dataGetPopup } = useGetPopupService();
  const { closePopupAsync } = useClosePopupService();
  const [isClosed, setIsClosed] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // The modal is mounted by the layout route, so `useParams` has no `:lang`
  // here and the language has to be read off the current path.
  const lang = pathname.split('/')[1] || 'az';

  const popup = dataGetPopup?.popup;
  // A popup already marked as read on the backend must not be shown again
  const isOpen = popup?.read === 'unread' && !isClosed;

  // The icon may come as a full URL or as a file name relative to base_url
  const iconUrl = popup?.icon?.startsWith('http')
    ? popup.icon
    : popup?.icon
      ? `${dataGetPopup?.base_url ?? ''}${popup.icon}`
      : '';

  const buttonText = popup?.button_text || t('homePage.popupAction');

  // Closing marks the popup as seen on the backend, so it is not served again.
  // A single click has to count as a double click, so the request is sent twice:
  // the second one always follows the first, whether it succeeded or failed.
  // mutateAsync is used because mutate's callbacks are dropped once the mutation
  // is replaced, which would swallow the second request.
  const closePopup = async () => {
    setIsClosed(true);

    if (!popup) return;

    const payload = { id: popup.id, customer_no: popup.customer_no };

    await closePopupAsync(payload).catch(() => undefined);
    await closePopupAsync(payload).catch(() => undefined);
  };

  const handleClose = () => closePopup();

  // The backend sends an app path without the language prefix ("/salary-card"),
  // so it is routed in place. Only absolute links point outside the app.
  const handleAction = () => {
    const link = popup?.notification_link;

    if (link) {
      if (/^https?:\/\//i.test(link)) {
        window.open(link, '_blank', 'noopener,noreferrer');
      } else {
        const segments = link.replace(/^\//, '').split('/');
        const path = segments[0] === lang ? segments : [lang, ...segments];

        startTransition(() => {
          navigate(`/${path.join('/')}`);
        });
      }
    }

    return closePopup();
  };

  return { t, popup, isOpen, iconUrl, buttonText, handleClose, handleAction };
};

export default useNotificationModal;
